import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { WatchlistItemLite } from "../../lib/otterfolio";
import { RenditebrueckeBadge } from "./RenditebrueckeBadge";
import { WatchlistItemFormModal } from "./WatchlistItemFormModal";
import { OTTERFOLIO_THEME } from "./theme";

interface Props {
  items: WatchlistItemLite[];
}

function fmt(cents: number | null, currency: string): string {
  if (cents === null) return "—";
  return new Intl.NumberFormat("de-DE", { style: "currency", currency }).format(cents / 100);
}

export function WatchlistSection({ items }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <section
      style={{
        marginTop: 24,
        background: OTTERFOLIO_THEME.cardBg,
        border: `1px solid ${OTTERFOLIO_THEME.cardBorder}`,
        borderRadius: 12,
        padding: 16,
      }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <h2 style={{ margin: 0, color: OTTERFOLIO_THEME.text, fontSize: 16 }}>
          {t("otterfolio.watchlist.sectionTitle", { defaultValue: "👀 Watchlist" })}
        </h2>
        <button type="button" onClick={() => setModalOpen(true)}>
          {t("otterfolio.watchlist.addButton", { defaultValue: "+ Watchlist-Item" })}
        </button>
      </header>

      {items.length === 0 ? (
        <div style={{ padding: 20, textAlign: "center", color: OTTERFOLIO_THEME.textMuted }}>
          {t("otterfolio.watchlist.empty", {
            defaultValue: "Noch keine Aktien auf der Watchlist.",
          })}
        </div>
      ) : (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {items.map((w) => (
            <li key={w.id}>
              <Link
                to={`/otterfolio/watchlist/${w.id}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto auto",
                  gap: 12,
                  alignItems: "center",
                  padding: "8px 10px",
                  border: `1px solid ${OTTERFOLIO_THEME.cardBorder}`,
                  borderRadius: 8,
                  color: OTTERFOLIO_THEME.text,
                  textDecoration: "none",
                  fontSize: 13,
                }}
              >
                <div>
                  <strong>{w.symbol}</strong>{" "}
                  <span style={{ color: OTTERFOLIO_THEME.textMuted }}>{w.name}</span>
                  {w.notes && (
                    <div style={{ fontSize: 11, color: OTTERFOLIO_THEME.textMuted, marginTop: 2 }}>
                      {w.notes}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: "right", fontWeight: 600 }}>
                  {fmt(w.currentPriceCents, w.currency)}
                </div>
                <div>
                  <RenditebrueckeBadge rb={w.renditebruecke} />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <WatchlistItemFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={() => qc.invalidateQueries({ queryKey: ["otterfolio", "overview"] })}
      />
    </section>
  );
}
